<template>
  <!-- The isGreen property explanation can be found further down below under "props" -->
  <div
    class="element-toolbar"
    :class="{
      'element-toolbar--green': isGreen,
      'element-toolbar--in-column': isColumnModel
    }"
  >
    <div class="element-toolbar__content">
      <div class="element-toolbar__delete" @click.stop="onDelete">
        <i class="icon-trash"></i>
      </div>
    </div>
    <teleport to="body">
      <transition name="modal" :appear="true">
        <Prompt
          v-if="showPrompt"
          :heading="promptHeading"
          :description="promptDescription"
          :buttons="promptButtons"
          @hidePrompt="showPrompt = false"
          @buttonClicked="handlePromptButtonClicked"
        />
      </transition>
    </teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue';
import { modalButtonId, ModelTypes } from '@/typings/types/types';
import { SectionBaseModel } from '@/components/layout/section/SectionBaseModel';
import RowModel from '@/components/layout/row/RowModel';
import ColumnModel from '@/components/layout/column/ColumnModel';
import AddonModel from '@/models/grid/AddonModel';
import Prompt from '@/components/components/prompt/Prompt.vue';
import { useStore } from '@/store';
import { AddonRegistrationModel } from '@/components/addons/registration/Model';
import { CampaignState } from '@/models/CampaignModel';
import { ModalButton } from '@/components/components/editing/Modals/interfaces';

export default defineComponent({
  name: 'DeleteElement',
  components: {
    Prompt
  },
  props: {
    model: {
      type: Object as PropType<ModelTypes>,
      required: true
    },
    /**
     * If this prop is true, the "trashcan" icon will become green. You can see this when marking/activating an addon
     */
    isGreen: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const store = useStore();
    const campaignState = store.getters['campaign/state'] as CampaignState;
    const showPrompt = ref(false);
    const isColumnModel = ref(false);

    const promptHeading = ref<string | undefined>();
    const promptDescription = ref<string | undefined>();
    const promptButtons = ref<ModalButton[]>([]);

    if (props.model instanceof ColumnModel) {
      isColumnModel.value = true;
    }

    const onDelete = () => {
      let hasRegistrationAddon = false;

      if (
        props.model instanceof SectionBaseModel ||
        props.model instanceof RowModel ||
        props.model instanceof ColumnModel
      ) {
        hasRegistrationAddon = props.model.getAddons<AddonRegistrationModel>('registration').length > 0;
      } else if (props.model.alias === 'registration') {
        hasRegistrationAddon = true;
      }

      let msg: string | undefined;
      if (hasRegistrationAddon && campaignState?.config?.hasRegistrations) {
        if (props.model instanceof SectionBaseModel)
          msg =
            'This campaign already has registrations therefore you are not able to delete this block because it include a registration form.';
        if (props.model instanceof RowModel)
          msg =
            'This campaign already has registrations therefore you are not able to delete this row because it include a registration form.';
        if (props.model instanceof ColumnModel)
          msg =
            'This campaign already has registrations therefore you are not able to delete this column because it include a registration form.';
        if (props.model instanceof AddonModel)
          msg = 'This campaign already has registrations therefore you are not able to delete the registration form.';

        if (msg) {
          promptDescription.value = msg;
        }
        promptButtons.value = [
          {
            id: 'no',
            variant: 'text',
            label: 'Close'
          }
        ];

        showPrompt.value = true;

        return;
      }

      if (props.model instanceof SectionBaseModel) msg = 'Are you sure you wish to delete this section?';
      if (props.model instanceof RowModel) msg = 'Are you sure you wish to delete this row?';
      if (props.model instanceof ColumnModel) msg = 'Are you sure you wish to delete this column?';
      if (props.model instanceof AddonModel) msg = 'Are you sure you wish to delete this addon?';

      if (msg) {
        promptHeading.value = msg;
      }
      promptButtons.value = [
        {
          id: 'yes',
          variant: 'text',
          label: 'Yes, delete'
        },
        {
          id: 'no',
          variant: 'text',
          label: 'No, cancel'
        }
      ];

      showPrompt.value = true;
    };

    const handlePromptButtonClicked = (id: modalButtonId) => {
      if (id === 'yes') {
        props.model.delete();
        store.dispatch('editing/removeActiveModel');
      }
      if (id === 'no') {
        showPrompt.value = false;
      }
    };

    return {
      isColumnModel,
      handlePromptButtonClicked,
      showPrompt,
      promptHeading,
      promptDescription,
      promptButtons,
      onDelete
    };
  }
});
</script>

<style lang="scss">
.grid__addon--position-fixed {
  .edit-title {
    transform: translateY(-100%);
  }
  .element-toolbar {
    transform: translateY(-100%);
    left: 61px;
    right: auto;
  }
}

.element-toolbar {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 100;
  display: block;
  width: 25px;
  height: 25px;
  background: $edit-border-color;
  line-height: 24px;
  color: #e3e3e3;
  font-size: 15px;
  cursor: pointer;
  text-align: center;
  float: right;
  transition: color 125ms linear;
  pointer-events: all;

  &--green {
    background: $edit-addon-border-color;
    color: #006349 !important;
    &:hover {
      color: #232424 !important;
    }
  }

  //&--in-column {
  //  right: 30px;
  //}

  &:hover {
    color: #fff;
  }
}
</style>
